import React, { useState, useEffect } from 'react';
import axios from 'axios';

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL || 'https://drvivara.cloud/api' });
api.interceptors.request.use(c => { const t = localStorage.getItem('token'); if (t) c.headers.Authorization = `Bearer ${t}`; return c; });

const ConsultationSettings = () => {
  const [pricing, setPricing] = useState({ video: 500, voice: 200, chat: 100 });
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [newPlan, setNewPlan] = useState({ name: '', amount: '', chat_limit: 0, voice_limit: 0, video_limit: 0, duration_days: 30 });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [pRes, plRes] = await Promise.all([
        api.get('/consultation-pricing'),
        api.get('/admin/subscriptions/plans?role=patient')
      ]);
      if (pRes.data.success) setPricing(pRes.data.data);
      if (plRes.data.success) setPlans(plRes.data.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const updatePricing = async () => {
    try {
      await api.put('/consultation-pricing', {
        video_price: pricing.video, voice_price: pricing.voice, chat_price: pricing.chat
      });
      setMessage('Pricing updated');
    } catch (e) { setMessage('Update failed'); }
  };

  const createPlan = async () => {
    if (!newPlan.name || !newPlan.amount) return;
    try {
      await api.post('/admin/subscriptions/plans', { ...newPlan, target_role: 'patient' });
      setMessage('Plan created');
      setNewPlan({ name: '', amount: '', chat_limit: 0, voice_limit: 0, video_limit: 0, duration_days: 30 });
      fetchData();
    } catch (e) { setMessage('Failed to create plan'); }
  };

  const deletePlan = async (id, name) => {
    if (!window.confirm(`Delete plan "${name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/subscriptions/plans/${id}`);
      setMessage('Plan deleted');
      fetchData();
    } catch (e) { setMessage('Failed to delete plan'); }
  };

  const togglePlanStatus = async (plan) => {
    try {
      await api.put(`/admin/subscriptions/plans/${plan._id}`, { is_active: !plan.is_active });
      setMessage(plan.is_active ? 'Plan deactivated' : 'Plan activated');
      fetchData();
    } catch (e) { setMessage('Failed to update plan'); }
  };

  if (loading) return <div className="p-8 text-center text-gold">Loading...</div>;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold text-gold mb-6">Consultation Settings</h1>
      {message && <p className="text-green-400 mb-4">{message}</p>}

      <div className="bg-gray-900 rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold text-light mb-4">Pay-Per-Use Pricing</h2>
        <div className="grid grid-cols-3 gap-4 mb-4">
          {['video', 'voice', 'chat'].map(type => (
            <div key={type}>
              <label className="text-sm text-gray-400 block capitalize">{type} Consultation (₹)</label>
              <input
                type="number" value={pricing[type]}
                onChange={(e) => setPricing(prev => ({ ...prev, [type]: e.target.value }))}
                className="w-full bg-gray-800 text-light border border-gray-600 rounded px-3 py-2 mt-1"
              />
            </div>
          ))}
        </div>
        <button onClick={updatePricing} className="bg-gold text-dark px-6 py-2 rounded font-semibold">Update Pricing</button>
      </div>

      <div className="bg-gray-900 rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold text-light mb-4">Patient Subscription Plans</h2>
        <div className="overflow-x-auto mb-4">
          <table className="w-full text-left text-sm">
            <thead><tr className="text-gray-400 border-b border-gray-700">
              <th className="py-2 px-3">Name</th><th className="py-2 px-3">Price</th>
              <th className="py-2 px-3">Video</th><th className="py-2 px-3">Voice</th>
              <th className="py-2 px-3">Chat</th>              <th className="py-2 px-3">Duration</th>
              <th className="py-2 px-3">Status</th>
              <th className="py-2 px-3">Actions</th>
            </tr></thead>
            <tbody>
              {plans.filter(p => p.target_role === 'patient').map(plan => (
                <tr key={plan._id} className="border-b border-gray-800">
                  <td className="py-2 px-3 text-light">{plan.name}</td>
                  <td className="py-2 px-3">₹{plan.amount}</td>
                  <td className="py-2 px-3">{plan.video_limit}</td>
                  <td className="py-2 px-3">{plan.voice_limit}</td>
                  <td className="py-2 px-3">{plan.chat_limit}</td>
                  <td className="py-2 px-3">{plan.duration_days}d</td>
                  <td className="py-2 px-3">
                    <span className={`px-2 py-0.5 rounded text-xs ${plan.is_active ? 'bg-green-600' : 'bg-red-600'}`}>
                      {plan.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="py-2 px-3">
                    <div className="flex gap-2">
                      <button onClick={() => togglePlanStatus(plan)}
                        className={`text-xs px-2 py-1 rounded ${plan.is_active ? 'bg-yellow-600' : 'bg-green-600'} text-light`}>
                        {plan.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                      <button onClick={() => deletePlan(plan._id, plan.name)}
                        className="text-xs px-2 py-1 rounded bg-red-600 text-light">
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-gray-900 rounded-lg p-6">
        <h2 className="text-xl font-semibold text-light mb-4">Create New Patient Plan</h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-gray-400 block">Plan Name</label>
            <input type="text" value={newPlan.name} onChange={(e) => setNewPlan(prev => ({ ...prev, name: e.target.value }))}
              className="w-full bg-gray-800 text-light border border-gray-600 rounded px-3 py-2 mt-1" />
          </div>
          <div>
            <label className="text-sm text-gray-400 block">Price (₹)</label>
            <input type="number" value={newPlan.amount} onChange={(e) => setNewPlan(prev => ({ ...prev, amount: e.target.value }))}
              className="w-full bg-gray-800 text-light border border-gray-600 rounded px-3 py-2 mt-1" />
          </div>
          <div>
            <label className="text-sm text-gray-400 block">Video Consultations</label>
            <input type="number" value={newPlan.video_limit} onChange={(e) => setNewPlan(prev => ({ ...prev, video_limit: parseInt(e.target.value) || 0 }))}
              className="w-full bg-gray-800 text-light border border-gray-600 rounded px-3 py-2 mt-1" />
          </div>
          <div>
            <label className="text-sm text-gray-400 block">Voice Consultations</label>
            <input type="number" value={newPlan.voice_limit} onChange={(e) => setNewPlan(prev => ({ ...prev, voice_limit: parseInt(e.target.value) || 0 }))}
              className="w-full bg-gray-800 text-light border border-gray-600 rounded px-3 py-2 mt-1" />
          </div>
          <div>
            <label className="text-sm text-gray-400 block">Chat Consultations</label>
            <input type="number" value={newPlan.chat_limit} onChange={(e) => setNewPlan(prev => ({ ...prev, chat_limit: parseInt(e.target.value) || 0 }))}
              className="w-full bg-gray-800 text-light border border-gray-600 rounded px-3 py-2 mt-1" />
          </div>
          <div>
            <label className="text-sm text-gray-400 block">Duration (days)</label>
            <input type="number" value={newPlan.duration_days} onChange={(e) => setNewPlan(prev => ({ ...prev, duration_days: parseInt(e.target.value) || 30 }))}
              className="w-full bg-gray-800 text-light border border-gray-600 rounded px-3 py-2 mt-1" />
          </div>
        </div>
        <button onClick={createPlan} className="bg-gold text-dark px-6 py-2 rounded mt-4 font-semibold">Create Plan</button>
      </div>
    </div>
  );
};

export default ConsultationSettings;
